import React, { useEffect, useMemo, useState } from "react";
import { api, downloadUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import AnalyticsCharts from "../components/AnalyticsCharts";
import DatePickerAZ from "../components/DatePickerAZ";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  FileSpreadsheet,
  FileText,
  Wallet,
  FolderKanban,
  Activity,
  TrendingUp,
  AlertOctagon,
  RefreshCw,
} from "lucide-react";

const KPI_ACCENTS = ["#004D99", "#FF5722", "#10B981", "#F59E0B", "#A855F7"];

const KPICard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div
    className="bg-white border border-slate-200 rounded-lg p-5 relative overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all"
    data-testid={testid}
  >
    <div
      className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-10"
      style={{ background: accent }}
    />
    <Icon className="w-5 h-5 mb-3 relative" style={{ color: accent }} />
    <div className="font-mono text-3xl font-black tracking-tight relative">
      {value}
    </div>
    <div className="label-overline mt-1 relative">{label}</div>
    {sub && <div className="text-[10px] mono text-slate-400 mt-1 relative">{sub}</div>}
  </div>
);

export default function Reports() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState("all");
  const [period, setPeriod] = useState("30");
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);
  const [customStart, setCustomStart] = useState(monthAgo);
  const [customEnd, setCustomEnd] = useState(today);
  const [refreshing, setRefreshing] = useState(false);

  const isCustom = period === "custom";

  const load = async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (isCustom) {
        params.start_date = customStart;
        params.end_date = customEnd;
      } else {
        params.days = Number(period);
      }
      if (projectFilter && projectFilter !== "all") {
        params.project_id = projectFilter;
      }
      const [a, p] = await Promise.all([
        api.get("/dashboard/analytics", { params }),
        api.get("/projects"),
      ]);
      setAnalytics(a.data);
      setProjects(p.data);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter, period, customStart, customEnd]);

  // Derived KPIs from server response
  const kpis = useMemo(() => {
    if (!analytics?.kpis) return null;
    const k = analytics.kpis;
    return {
      totalCost: k.total_cost || 0,
      totalProjects: k.total_projects || 0,
      activeInspections: k.active_inspections || 0,
      totalInsp: k.total_inspections || 0,
      avgRating: k.avg_contractor_rating || 0,
      nokPct: k.nok_pct || 0,
    };
  }, [analytics]);

  if (!analytics)
    return <div className="mono text-sm text-slate-500">Yüklənir...</div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-overline mb-1">analitik mərkəz</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">
            Hesabatlar
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            DESN-2021-27 əsaslı performans, smeta və yoxlanış göstəriciləri
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger
              className="w-[160px] bg-white"
              data-testid="period-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Son 7 gün</SelectItem>
              <SelectItem value="14">Son 14 gün</SelectItem>
              <SelectItem value="30">Son 30 gün</SelectItem>
              <SelectItem value="90">Son 90 gün</SelectItem>
              <SelectItem value="custom">Xüsusi aralıq</SelectItem>
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger
              className="w-[220px] bg-white"
              data-testid="project-filter"
            >
              <SelectValue placeholder="Bütün layihələr" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Bütün layihələr</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name.length > 32 ? p.name.slice(0, 32) + "…" : p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={load}
            disabled={refreshing}
            data-testid="reports-refresh"
            title="Yenilə"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
          <a
            href={
              projectFilter === "all"
                ? downloadUrl("/reports/projects.xlsx")
                : downloadUrl(`/projects/${projectFilter}/report.xlsx`)
            }
            className="inline-flex items-center gap-2 px-3 py-2 text-xs mono uppercase tracking-wide rounded-md border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 transition-colors"
            data-testid="reports-export-xlsx"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </a>
          {projectFilter !== "all" && (
            <a
              href={downloadUrl(`/projects/${projectFilter}/report.pdf`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs mono uppercase tracking-wide rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-50 transition-colors"
              data-testid="reports-export-pdf"
            >
              <FileText className="w-4 h-4" /> PDF
            </a>
          )}
        </div>
      </div>

      {/* Custom date range row */}
      {isCustom && (
        <div
          className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 flex flex-wrap items-center gap-3"
          data-testid="custom-range-row"
        >
          <span className="label-overline text-slate-500">tarix aralığı:</span>
          <div className="w-52">
            <DatePickerAZ
              value={customStart}
              onChange={setCustomStart}
              placeholder="Başlanğıc"
              testid="custom-start"
              maxDate={customEnd ? new Date(customEnd) : new Date()}
            />
          </div>
          <span className="text-slate-400 mono">→</span>
          <div className="w-52">
            <DatePickerAZ
              value={customEnd}
              onChange={setCustomEnd}
              placeholder="Son"
              testid="custom-end"
              minDate={customStart ? new Date(customStart) : undefined}
              maxDate={new Date()}
            />
          </div>
          <span
            className="mono text-[10px] text-slate-500 uppercase tracking-wider"
            data-testid="custom-range-info"
          >
            {analytics?.filters?.days ? `${analytics.filters.days} gün` : ""}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              const t = new Date().toISOString().slice(0, 10);
              const m = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);
              setCustomStart(m);
              setCustomEnd(t);
              setPeriod("30");
            }}
            data-testid="custom-range-reset"
            className="ml-auto text-xs mono uppercase tracking-wide"
          >
            Sıfırla
          </Button>
        </div>
      )}

      {/* KPI grid */}
      {kpis && (
        <div
          className="grid grid-cols-2 lg:grid-cols-5 gap-4"
          data-testid="reports-kpis"
        >
          <KPICard
            icon={Wallet}
            label="Cəmi smeta"
            value={`${(kpis.totalCost / 1000).toFixed(1)}K ₼`}
            sub={`${kpis.totalCost.toLocaleString()} ₼`}
            accent={KPI_ACCENTS[0]}
            testid="kpi-cost"
          />
          <KPICard
            icon={FolderKanban}
            label="Layihələr"
            value={kpis.totalProjects}
            sub="aktiv portfel"
            accent={KPI_ACCENTS[1]}
            testid="kpi-projects"
          />
          <KPICard
            icon={Activity}
            label="Aktiv yoxlanış"
            value={kpis.activeInspections}
            sub={`${kpis.totalInsp} cəmi`}
            accent={KPI_ACCENTS[2]}
            testid="kpi-active"
          />
          <KPICard
            icon={TrendingUp}
            label="Orta reytinq"
            value={`${kpis.avgRating.toFixed(0)}%`}
            sub="podratçılar"
            accent={KPI_ACCENTS[3]}
            testid="kpi-rating"
          />
          <KPICard
            icon={AlertOctagon}
            label="NOT OK nisbəti"
            value={`${kpis.nokPct.toFixed(1)}%`}
            sub="cəmi yoxlanışlara görə"
            accent={KPI_ACCENTS[4]}
            testid="kpi-nok"
          />
        </div>
      )}

      {/* Charts */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-xl">Analitik göstəricilər</h2>
          <span className="mono text-[10px] text-slate-500 uppercase tracking-wider">
            real-time · {user?.role}
          </span>
        </div>
        <AnalyticsCharts data={analytics} />
      </section>

      {/* Project leaderboard */}
      <section
        className="bg-white border border-slate-200 rounded-lg p-6"
        data-testid="reports-leaderboard"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl">Layihə icra cədvəli</h2>
          <span className="mono text-[10px] text-slate-500 uppercase">
            {analytics.project_chart?.length || 0} layihə
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 mono">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-2">Layihə</th>
                <th className="text-right py-2 px-2">Uzunluq (km)</th>
                <th className="text-right py-2 px-2">Mərhələ</th>
                <th className="text-right py-2 px-2">Smeta (₼)</th>
                <th className="text-right py-2 px-2 w-44">İcra</th>
              </tr>
            </thead>
            <tbody>
              {(analytics.project_chart || []).map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="py-2 px-2 font-medium">{p.short_name}</td>
                  <td className="py-2 px-2 text-right mono">{p.length_km}</td>
                  <td className="py-2 px-2 text-right mono">{p.stages}</td>
                  <td className="py-2 px-2 text-right mono font-bold">
                    {p.cost.toLocaleString()}
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded">
                        <div
                          className="h-full rounded transition-all"
                          style={{
                            width: `${p.completion}%`,
                            background:
                              p.completion >= 75
                                ? "#10B981"
                                : p.completion >= 25
                                ? "#F59E0B"
                                : "#EF4444",
                          }}
                        />
                      </div>
                      <span className="mono text-xs w-8 text-right">
                        {p.completion}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              {!analytics.project_chart?.length && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-6 text-center text-slate-400 mono"
                  >
                    Layihə yoxdur
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Contractor leaderboard */}
      <section
        className="bg-white border border-slate-200 rounded-lg p-6"
        data-testid="reports-contractor-leaderboard"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl">Podratçı reytinq cədvəli</h2>
          <span className="mono text-[10px] text-slate-500 uppercase">
            OK / (OK + NOT OK) × 100%
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {(analytics.contractor_chart || []).map((c, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-4 p-3 rounded-md border border-slate-200 hover:border-[#004D99] transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-7 h-7 rounded font-mono text-xs font-black flex items-center justify-center text-white shrink-0"
                  style={{
                    background:
                      c.rating >= 75
                        ? "#10B981"
                        : c.rating >= 50
                        ? "#F59E0B"
                        : "#EF4444",
                  }}
                >
                  {i + 1}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{c.name}</div>
                  <div className="text-[10px] mono text-slate-500">
                    OK {c.ok} · NOT OK {c.not_ok}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="mono text-2xl font-black">
                  {c.rating.toFixed(0)}
                  <span className="text-sm text-slate-400">%</span>
                </div>
              </div>
            </div>
          ))}
          {!analytics.contractor_chart?.length && (
            <div className="col-span-full text-center py-6 text-slate-400 mono">
              Reytinq məlumatı yoxdur
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
